"use client";
import React from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowRight, Star, Check, X as XIcon, Minus } from "lucide-react";

interface Platform {
 rank: number;
 name: string;
 shortName?: string;
 tagline: string;
 best: string;
 description: string;
 pros: string[];
 cons: string[];
 features: Record<string, boolean | "partial">;
 verdict: string;
 highlight?: boolean;
}

const PLATFORMS: Platform[] = [
 {
 rank: 1,
 name: "The Yard",
 shortName: "Yard",
 tagline: "The all-in-one campus marketing platform",
 best: "Best overall for college influencer marketing",
 description:
 "The Yard was built from the ground up to connect brands with college students, student organizations, and campus creators. Unlike general-purpose influencer platforms, every feature is designed around the college ecosystem - from greek life partnerships and student org sponsorships to individual creator campaigns. Brands can discover creators by school, major, and campus involvement, then manage campaigns, track deliverables, and handle payments in one place.",
 pros: [
 "Purpose-built for college campuses - not a general influencer tool repurposed",
 "Connects brands with student orgs, greek life, athletes, and individual creators",
 "Students and orgs can create profiles, showcase portfolios, and apply to campaigns",
 "Integrated campaign management with applications, invitations, and deliverable tracking",
 "Free for students and student organizations",
 "Growing network across top universities (Harvard, MIT, Northeastern, BU, and more)",
 ],
 cons: [
 "Newer platform, still expanding to more campuses",
 ],
 features: {
 "Campus-specific discovery": true,
 "Student org partnerships": true,
 "Campaign management": true,
 "Creator portfolios": true,
 "In-app messaging": true,
 "Free for creators": true,
 "Built for Gen Z": true,
 },
 verdict:
 "If you\u2019re a brand looking to reach college students authentically, The Yard is the clear winner. It\u2019s the only platform built specifically for campus marketing, and the ability to work with student orgs and individual creators in one place is unmatched.",
 highlight: true,
 },
 {
 rank: 2,
 name: "Upfluence",
 tagline: "Enterprise influencer marketing suite",
 best: "Best for large brands with big budgets",
 description:
 "Upfluence is a full-featured influencer marketing platform used by enterprise brands. It offers a massive influencer database, campaign workflow tools, and deep analytics. However, it\u2019s built for general influencer marketing - there\u2019s no campus-specific filtering or student org integration.",
 pros: [
 "Massive influencer database with detailed search filters",
 "Strong analytics and ROI tracking",
 "E-commerce integrations (Shopify, WooCommerce)",
 ],
 cons: [
 "No campus or university-specific features",
 "Expensive - enterprise pricing starts high",
 "Overkill for brands targeting college audiences specifically",
 "No student org or greek life partnerships",
 ],
 features: {
 "Campus-specific discovery": false,
 "Student org partnerships": false,
 "Campaign management": true,
 "Creator portfolios": "partial",
 "In-app messaging": true,
 "Free for creators": false,
 "Built for Gen Z": false,
 },
 verdict:
 "Great for enterprise brands running broad influencer campaigns, but not ideal if your goal is authentic college campus activations.",
 },
 {
 rank: 3,
 name: "Home From College",
 shortName: "HFC",
 tagline: "Professional development platform for Gen Z",
 best: "Best for connecting with college students through gigs",
 description:
 "Home From College (HFC) is a professional development platform built specifically for Gen Z college students. It connects brands with students through a GIGs marketplace, where students can take on projects and brand opportunities while building their professional profiles. It\u2019s genuinely college-focused, which sets it apart from the general influencer platforms on this list. However, it\u2019s more of a career and gig platform than a dedicated influencer marketing tool - so campaign management, content tracking, and org-level partnerships aren\u2019t the primary focus.",
 pros: [
 "Built specifically for Gen Z college students",
 "GIGs marketplace connects brands directly with student talent",
 "Students build professional profiles and gain real experience",
 "Free for students to join and participate",
 ],
 cons: [
 "More career/gig-focused than influencer marketing-focused",
 "No student org sponsorship or greek life integration",
 "Limited campaign management and deliverable tracking tools",
 "No content rights management or UGC workflow features",
 ],
 features: {
 "Campus-specific discovery": true,
 "Student org partnerships": false,
 "Campaign management": "partial",
 "Creator portfolios": true,
 "In-app messaging": "partial",
 "Free for creators": true,
 "Built for Gen Z": true,
 },
 verdict:
 "HFC is a solid platform if you want to connect with college students for gig-style work and brand projects. But if you need full influencer campaign management, student org partnerships, or content tracking, you\u2019ll want a more marketing-focused tool.",
 },
 {
 rank: 4,
 name: "Collabstr",
 tagline: "Influencer marketplace for content creators",
 best: "Best for quick one-off collaborations",
 description:
 "Collabstr is a marketplace where brands can browse influencer profiles and book collaborations directly. It\u2019s simple and transactional - think Fiverr for influencer marketing. While some college-age creators are on the platform, there\u2019s no way to filter by campus, major, or student involvement.",
 pros: [
 "Simple, easy-to-use marketplace model",
 "Transparent pricing on creator profiles",
 "No minimum spend or long-term contracts",
 ],
 cons: [
 "No campus-specific search or filtering",
 "Very transactional - limited relationship building",
 "No student org sponsorship features",
 "Limited campaign management tools",
 ],
 features: {
 "Campus-specific discovery": false,
 "Student org partnerships": false,
 "Campaign management": "partial",
 "Creator portfolios": true,
 "In-app messaging": true,
 "Free for creators": true,
 "Built for Gen Z": "partial",
 },
 verdict:
 "Good for quick, one-off influencer bookings. But if you need campus-level targeting or ongoing student partnerships, you\u2019ll need something more specialized.",
 },
 {
 rank: 5,
 name: "GRIN",
 tagline: "Creator management for DTC brands",
 best: "Best for DTC e-commerce brands",
 description:
 "GRIN is a creator management platform designed for direct-to-consumer brands. It excels at product seeding, affiliate tracking, and managing long-term creator relationships. It\u2019s powerful but geared toward e-commerce - campus marketing isn\u2019t part of the equation.",
 pros: [
 "Excellent for product seeding and gifting workflows",
 "Strong affiliate and revenue tracking",
 "Good for managing ongoing creator relationships",
 ],
 cons: [
 "No college or campus-specific features",
 "High price point - designed for established DTC brands",
 "Steep learning curve",
 "No student org integration",
 ],
 features: {
 "Campus-specific discovery": false,
 "Student org partnerships": false,
 "Campaign management": true,
 "Creator portfolios": "partial",
 "In-app messaging": true,
 "Free for creators": false,
 "Built for Gen Z": false,
 },
 verdict:
 "If you\u2019re a DTC brand focused on product seeding and affiliate revenue, GRIN is strong. But it won\u2019t help you run campus activations or partner with student organizations.",
 },
 {
 rank: 6,
 name: "Aspire (AspireIQ)",
 tagline: "Community-driven influencer marketing",
 best: "Best for building creator communities",
 description:
 "Aspire focuses on building long-term creator communities rather than one-off campaigns. Brands can recruit, manage, and nurture relationships with creators at scale. While it\u2019s a solid platform, the college market isn\u2019t a focus area.",
 pros: [
 "Strong community-building and ambassador program tools",
 "Good content management and rights tracking",
 "Scales well for brands with many creator partners",
 ],
 cons: [
 "No university or campus-specific targeting",
 "Better suited for established creator programs than campus marketing",
 "Premium pricing",
 "No student org or event sponsorship features",
 ],
 features: {
 "Campus-specific discovery": false,
 "Student org partnerships": false,
 "Campaign management": true,
 "Creator portfolios": "partial",
 "In-app messaging": true,
 "Free for creators": false,
 "Built for Gen Z": false,
 },
 verdict:
 "Aspire is a top-tier community management tool, but if you\u2019re specifically trying to activate on college campuses, it\u2019s not built for that.",
 },
 {
 rank: 7,
 name: "Statusphere",
 shortName: "Status",
 tagline: "Automated micro-influencer marketing",
 best: "Best for automated product gifting",
 description:
 "Statusphere automates micro-influencer marketing by matching brands with creators and handling product shipments. It\u2019s hands-off by design - the platform picks the creators for you based on your criteria. Some college students may be in their network, but there\u2019s no campus-level control.",
 pros: [
 "Fully automated creator matching and product shipping",
 "Low effort for brands - very hands-off",
 "Focus on authentic micro-influencers",
 ],
 cons: [
 "No control over which specific creators you work with",
 "No campus, university, or student org targeting",
 "Limited to product gifting - no event or sponsorship support",
 "Less transparency into who\u2019s posting about you",
 ],
 features: {
 "Campus-specific discovery": false,
 "Student org partnerships": false,
 "Campaign management": "partial",
 "Creator portfolios": false,
 "In-app messaging": false,
 "Free for creators": true,
 "Built for Gen Z": "partial",
 },
 verdict:
 "A good \u201cset it and forget it\u201d option for product gifting, but you sacrifice control and can\u2019t target college campuses specifically.",
 },
 {
 rank: 8,
 name: "Hashtag Paid (#paid)",
 shortName: "#paid",
 tagline: "Creator marketplace with opt-in campaigns",
 best: "Best for creator-first brand partnerships",
 description:
 "Hashtag Paid flips the script by letting creators opt into brand campaigns rather than brands reaching out. It\u2019s a quality-over-quantity approach that results in more authentic partnerships. However, the college segment isn\u2019t a dedicated focus.",
 pros: [
 "Creators opt in, leading to more authentic content",
 "Handshake\u2122 matching technology pairs brands with relevant creators",
 "Good for brands wanting premium, curated partnerships",
 ],
 cons: [
 "Smaller creator pool than other platforms",
 "No campus or university-specific features",
 "Higher cost per creator engagement",
 "No student org or event marketing capabilities",
 ],
 features: {
 "Campus-specific discovery": false,
 "Student org partnerships": false,
 "Campaign management": true,
 "Creator portfolios": true,
 "In-app messaging": true,
 "Free for creators": true,
 "Built for Gen Z": false,
 },
 verdict:
 "A premium marketplace for brands wanting authentic creator content, but not the right tool for targeted college marketing.",
 },
];

function FeatureIcon({ value }: { value: boolean | "partial" }) {
 if (value === true) return <Check className="w-4 h-4 text-yc-5ee5c5" />;
 if (value === "partial") return <Minus className="w-4 h-4 text-amber-400" />;
 return <XIcon className="w-4 h-4 text-white/20" />;
}

export default function BestCollegeInfluencerMarketingPlatforms() {
 const featureKeys = Object.keys(PLATFORMS[0].features);

 return (
 <>
 {/* Intro */}
 <div className="prose-section mb-12">
 <p className="text-white/70 text-base md:text-lg leading-relaxed mb-6">
 College students are the most coveted demographic in marketing. They&apos;re early adopters, trendsetters,
 and at the stage of life where they&apos;re forming the brand loyalties that will stick for decades. But reaching
 them authentically? That&apos;s the hard part.
 </p>
 <p className="text-white/70 text-base md:text-lg leading-relaxed mb-6">
 Traditional influencer marketing platforms are built for broad audiences. They help brands find creators
 with 100K+ followers across Instagram and TikTok, but they don&apos;t understand the <em className="text-white/90 not-italic font-medium">campus ecosystem</em> - 
 greek life, student orgs, campus events, dorm culture, and the micro-communities where real influence lives.
 </p>
 <p className="text-white/70 text-base md:text-lg leading-relaxed">
 We tested and compared the top platforms to find the best options for brands that want to market
 on college campuses. Here&apos;s what we found.
 </p>
 </div>

 {/* Quick Comparison Table */}
 <div className="mb-16">
 <h2 className="text-xl md:text-2xl font-semibold text-white mb-6">Quick Comparison</h2>
 <div className="overflow-x-auto -mx-6 px-6">
 <div className="min-w-[780px]">
 <div className="rounded-xl border border-white/[0.08] overflow-hidden">
 {/* Header */}
 <div className="grid grid-cols-[1fr_repeat(8,72px)] bg-white/[0.03] border-b border-white/[0.06]">
 <div className="p-3 text-xs font-medium text-white/55 uppercase tracking-wider">Feature</div>
 {PLATFORMS.map((p) => (
 <div
 key={p.name}
 className={`p-2 text-center text-[10px] font-medium uppercase tracking-wider leading-tight ${
 p.highlight ? "text-yc-5ee5c5 bg-yc-5ee5c5/[0.05]" : "text-white/55"
 }`}
 >
 {p.shortName || p.name.split(" ")[0]}
 </div>
 ))}
 </div>
 {/* Rows */}
 {featureKeys.map((feature, i) => (
 <div
 key={feature}
 className={`grid grid-cols-[1fr_repeat(8,72px)] ${
 i < featureKeys.length - 1 ? "border-b border-white/[0.04]" : ""
 }`}
 >
 <div className="p-3 text-sm text-white/60">{feature}</div>
 {PLATFORMS.map((p) => (
 <div
 key={p.name}
 className={`p-3 flex items-center justify-center ${
 p.highlight ? "bg-yc-5ee5c5/[0.03]" : ""
 }`}
 >
 <FeatureIcon value={p.features[feature]} />
 </div>
 ))}
 </div>
 ))}
 </div>
 </div>
 </div>
 <p className="text-xs text-white/45 mt-3 flex items-center gap-3">
 <span className="flex items-center gap-1"><Check className="w-3 h-3 text-yc-5ee5c5" /> Yes</span>
 <span className="flex items-center gap-1"><Minus className="w-3 h-3 text-amber-400" /> Partial</span>
 <span className="flex items-center gap-1"><XIcon className="w-3 h-3 text-white/20" /> No</span>
 </p>
 </div>

 {/* Platform Reviews */}
 <div className="space-y-10">
 {PLATFORMS.map((platform) => (
 <div
 key={platform.name}
 className={`rounded-2xl border p-6 md:p-8 transition-all ${
 platform.highlight
 ? "border-yc-5ee5c5/30 bg-gradient-to-br from-yc-5ee5c5/[0.04] to-transparent"
 : "border-white/[0.08] bg-white/[0.02]"
 }`}
 >
 {/* Rank + Name */}
 <div className="flex items-start gap-4 mb-4">
 <div
 className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold ${
 platform.highlight
 ? "bg-yc-5ee5c5 text-yc-050f0d"
 : "bg-white/[0.06] text-white/60"
 }`}
 >
 {platform.rank}
 </div>
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 flex-wrap">
 <h3 className="text-xl md:text-2xl font-semibold text-white">{platform.name}</h3>
 {platform.highlight && (
 <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-yc-5ee5c5/15 text-yc-5ee5c5 border border-yc-5ee5c5/25">
 <Star className="w-3 h-3" /> Top Pick
 </span>
 )}
 </div>
 <p className="text-sm text-white/55 mt-0.5">{platform.best}</p>
 </div>
 </div>

 <p className="text-white/60 text-sm md:text-base leading-relaxed mb-6">
 {platform.description}
 </p>

 {/* Pros & Cons */}
 <div className="grid md:grid-cols-2 gap-4 mb-6">
 <div className="space-y-2">
 <p className="text-xs font-medium text-yc-5ee5c5/80 uppercase tracking-wider mb-2">Pros</p>
 {platform.pros.map((pro) => (
 <div key={pro} className="flex gap-2 text-sm text-white/60">
 <Check className="w-4 h-4 text-yc-5ee5c5 flex-shrink-0 mt-0.5" />
 <span>{pro}</span>
 </div>
 ))}
 </div>
 <div className="space-y-2">
 <p className="text-xs font-medium text-red-400/80 uppercase tracking-wider mb-2">Cons</p>
 {platform.cons.map((con) => (
 <div key={con} className="flex gap-2 text-sm text-white/60">
 <XIcon className="w-4 h-4 text-red-400/60 flex-shrink-0 mt-0.5" />
 <span>{con}</span>
 </div>
 ))}
 </div>
 </div>

 {/* Verdict */}
 <div
 className={`rounded-xl p-4 text-sm leading-relaxed ${
 platform.highlight
 ? "bg-yc-5ee5c5/[0.08] border border-yc-5ee5c5/20 text-white/80"
 : "bg-white/[0.03] border border-white/[0.06] text-white/60"
 }`}
 >
 <span className="font-medium text-white/90">Verdict: </span>
 {platform.verdict}
 </div>

 {platform.highlight && (
 <div className="mt-6 flex justify-center">
 <Link
 href="/get-in"
 className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold bg-yc-5ee5c5 hover:bg-yc-4dd4b4 text-yc-050f0d transition-all"
 >
 Get started with The Yard
 <ArrowRight className="w-4 h-4" />
 </Link>
 </div>
 )}
 </div>
 ))}
 </div>

 {/* Conclusion */}
 <div className="mt-16 mb-12">
 <h2 className="text-xl md:text-2xl font-semibold text-white mb-6">The Bottom Line</h2>
 <p className="text-white/70 text-base md:text-lg leading-relaxed mb-6">
 If you&apos;re a brand trying to reach college students, the platform you choose matters. General influencer
 marketing tools can help you find creators with big followings, but they don&apos;t understand the campus
 ecosystem - the student orgs, the campus events, the micro-communities that drive real influence among
 Gen Z.
 </p>
 <p className="text-white/70 text-base md:text-lg leading-relaxed mb-6">
 <strong className="text-white">The Yard</strong> is the only platform on this list built specifically for
 college marketing. It connects brands with student creators, student organizations, greek life chapters,
 and campus athletes - all in one place. Whether you want to sponsor an event, run a UGC campaign,
 or build long-term ambassador relationships on campus, The Yard makes it simple.
 </p>
 <p className="text-white/70 text-base md:text-lg leading-relaxed">
 The other platforms on this list are strong in their own categories. But if college campuses are your
 target, there&apos;s a clear front-runner.
 </p>
 </div>

 {/* CTA */}
 <div className="relative rounded-2xl border border-yc-5ee5c5/20 overflow-hidden p-8 md:p-10 text-center">
 <Image
 src="/banner-home.jpg"
 alt=""
 fill
 className="object-cover object-center"
 />
 <div className="absolute inset-0 bg-yc-050f0d/80" />
 <div className="absolute inset-0 bg-gradient-to-br from-yc-5ee5c5/[0.08] to-transparent" />
 <div className="relative z-10">
 <h3 className="text-2xl md:text-3xl font-medium text-white mb-3">
 Ready to reach college campuses?
 </h3>
 <p className="text-white/60 text-base md:text-lg mb-6 max-w-lg mx-auto">
 Join The Yard and connect with student creators and organizations at top universities.
 </p>
 <Link
 href="/get-in"
 className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-semibold bg-yc-5ee5c5 hover:bg-yc-4dd4b4 text-yc-050f0d transition-all"
 >
 Join The Yard
 <ArrowRight className="w-4 h-4" />
 </Link>
 </div>
 </div>
 </>
 );
}
